/**
 * Komisyon Oranları Ayarlar Sayfası (Tarihsel Dönem yönetimi)
 *
 * Yaklaşım A: tek tablo `odeme_komisyon_oranlari`, kanal kimliği = pos_odeme_tipi.
 * Her satır bir (kanal, dönem). Oran değişimi geçmişi BOZMAZ çünkü:
 *  - Oran değişince "Yeni dönem oranı" eklenir (eski dönem aktif kalır).
 *  - komisyon_service her satışa tarihine göre doğru oranı uygular.
 *  - Satışı olan dönemin oranı yerinde DEĞİŞTİRİLEMEZ (backend guard).
 *
 * Anayasa: M.1 (DB-driven) · M.2 (CRUD bütünlüğü) · M.3 (kod immutable) ·
 *          M.5 (çift katman yetki) · M.6 (soft default + usage guard).
 *
 * Backend:
 *  - GET    /api/komisyon-oranlari?gruplu=true   → kanal + güncel oran + dönem geçmişi
 *  - POST   /api/komisyon-oranlari               → yeni kanal
 *  - POST   /api/komisyon-oranlari/yeni-donem    → tarihsel dönem ekle
 *  - PATCH  /api/komisyon-oranlari/{id}          → ad/aktif (oran kilidi satış varsa)
 *  - DELETE /api/komisyon-oranlari/{id}[?hard=true]
 */

import React, { useEffect, useState } from 'react';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import { styles, colors, radius } from '../../styles/theme';
import { normalizeDecimalInput, parseDecimal } from '../../utils/decimalInput';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Backend KOMISYON_KATEGORILERI sistem enum'unu yansıtır (P&L bucket'ı; müşteriye
// göre değişmeyen yapısal sınıflama — esikler tip UST/ALT gibi).
const KATEGORI_OPTIONS = [
  { value: 'YEMEK_KARTI', label: 'Yemek Kartı' },
  { value: 'BANKA_KART', label: 'Banka / Kredi Kartı' },
  { value: 'ONLINE_PLATFORM', label: 'Online Platform' },
  { value: 'NAKIT', label: 'Nakit' },
];
const kategoriLabel = (v) => (KATEGORI_OPTIONS.find((o) => o.value === v) || {}).label || v;

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '13px',
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, color: '#666', display: 'block', marginBottom: 4 };
const fmtPct = (v) => `%${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;

const EMPTY_CHANNEL = {
  kod: '',
  ad: '',
  pos_odeme_tipi: '',
  kategori: 'YEMEK_KARTI',
  komisyon_pct: '',
  gecerlilik_baslangic: '',
};

const KomisyonOranlariPage = () => {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = hasPermission(user?.role, PERMISSIONS.MANAGE_SETTINGS, user?.effectivePermissions);

  const [kanallar, setKanallar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({}); // {pos_odeme_tipi: bool}

  const [channelModal, setChannelModal] = useState(false);
  const [channelForm, setChannelForm] = useState({ ...EMPTY_CHANNEL });
  const [channelError, setChannelError] = useState('');

  const [periodModal, setPeriodModal] = useState(null); // {kanal}
  const [periodForm, setPeriodForm] = useState({ komisyon_pct: '', gecerlilik_baslangic: '' });
  const [periodError, setPeriodError] = useState('');

  const [editModal, setEditModal] = useState(null); // {id, kod, ad, aktif}
  const [editError, setEditError] = useState('');

  // ─── Veri yükleme ──────────────────────────────────────────────
  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/komisyon-oranlari?gruplu=true`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => []);
      setKanallar(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Komisyon oranları yüklenemedi:', err);
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Yeni kanal ────────────────────────────────────────────────
  const openChannel = () => {
    setChannelForm({ ...EMPTY_CHANNEL });
    setChannelError('');
    setChannelModal(true);
  };

  const submitChannel = async () => {
    const { kod, ad, pos_odeme_tipi, kategori, komisyon_pct, gecerlilik_baslangic } = channelForm;
    if (!kod.trim()) return setChannelError('Kod zorunludur.');
    if (!ad.trim()) return setChannelError('Ad zorunludur.');
    if (!pos_odeme_tipi.trim()) return setChannelError('POS ödeme tipi zorunludur (modpos string ile birebir eşleşmeli).');
    if (!gecerlilik_baslangic) return setChannelError('Geçerlilik başlangıç tarihi zorunludur.');
    const pct = parseDecimal(komisyon_pct, NaN);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) return setChannelError('Komisyon yüzdesi 0-100 arası olmalı.');

    setSaving(true);
    setChannelError('');
    try {
      const res = await fetch(`${API_URL}/api/komisyon-oranlari`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kod: kod.trim(),
          ad: ad.trim(),
          pos_odeme_tipi: pos_odeme_tipi.trim(),
          kategori,
          komisyon_pct: pct,
          gecerlilik_baslangic,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Kanal eklendi');
      setChannelModal(false);
      fetchAll();
    } catch (err) {
      setChannelError(err.message || 'Eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Yeni dönem oranı ──────────────────────────────────────────
  const openPeriod = (kanal) => {
    setPeriodModal({ kanal });
    setPeriodForm({ komisyon_pct: '', gecerlilik_baslangic: '' });
    setPeriodError('');
  };

  const submitPeriod = async () => {
    if (!periodModal) return;
    const { komisyon_pct, gecerlilik_baslangic } = periodForm;
    if (!gecerlilik_baslangic) return setPeriodError('Geçerlilik başlangıç tarihi zorunludur.');
    const pct = parseDecimal(komisyon_pct, NaN);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) return setPeriodError('Komisyon yüzdesi 0-100 arası olmalı.');

    setSaving(true);
    setPeriodError('');
    try {
      const res = await fetch(`${API_URL}/api/komisyon-oranlari/yeni-donem`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kaynak_id: periodModal.kanal.guncel_id || periodModal.kanal.id,
          komisyon_pct: pct,
          gecerlilik_baslangic,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Yeni dönem oranı eklendi (geçmiş korundu)');
      setPeriodModal(null);
      fetchAll();
    } catch (err) {
      setPeriodError(err.message || 'Eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Düzenle (ad + aktif) ──────────────────────────────────────
  const openEdit = (kanal) => {
    setEditModal({ id: kanal.guncel_id || kanal.id, kod: kanal.kod, ad: kanal.ad || '', aktif: kanal.aktif !== false });
    setEditError('');
  };

  const submitEdit = async () => {
    if (!editModal) return;
    if (!editModal.ad.trim()) return setEditError('Ad zorunludur.');
    setSaving(true);
    setEditError('');
    try {
      // NOT: oran/geçerlilik bilinçli olarak gönderilmez — oran değişimi 'Yeni dönem'.
      const res = await fetch(`${API_URL}/api/komisyon-oranlari/${editModal.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ad: editModal.ad.trim(), aktif: editModal.aktif }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Güncellendi');
      setEditModal(null);
      fetchAll();
    } catch (err) {
      setEditError(err.message || 'Güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Pasifleştir / Aktifleştir (güncel dönem) ──────────────────
  const togglePasif = async (kanal) => {
    const id = kanal.guncel_id || kanal.id;
    const isAktif = kanal.aktif !== false;
    try {
      let res;
      if (isAktif) {
        if (!window.confirm(`"${kanal.ad}" kanalı pasifleştirilsin mi? (güncel dönem; geri alınabilir)`)) return;
        res = await fetch(`${API_URL}/api/komisyon-oranlari/${id}`, { method: 'DELETE', headers: authHeaders() });
      } else {
        res = await fetch(`${API_URL}/api/komisyon-oranlari/${id}`, {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ aktif: true }),
        });
      }
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success(isAktif ? 'Pasifleştirildi' : 'Aktifleştirildi');
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'İşlem başarısız');
    }
  };

  // ─── Dönem sil (hard, usage=0) ─────────────────────────────────
  const deletePeriod = async (donem) => {
    if ((donem.usage || 0) > 0) {
      toast.warning(`Bu dönemde ${donem.usage} satış var — kalıcı silinemez.`);
      return;
    }
    if (!window.confirm(`"${donem.kod}" dönemi KALICI silinecek. Geri alınamaz. Devam?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/komisyon-oranlari/${donem.id}?hard=true`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Dönem silindi');
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'Silinemedi');
    }
  };

  // ─── Render ────────────────────────────────────────────────────
  if (!canManage) {
    return (
      <div>
        <Header title="Komisyon Oranları" />
        <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
          Bu sayfayı görüntüleme yetkiniz yok. (MANAGE_SETTINGS gerekli)
        </div>
      </div>
    );
  }

  if (loading && kanallar.length === 0) {
    return (
      <div>
        <Header title="Komisyon Oranları" />
        <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Komisyon Oranları" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        <div
          style={{
            background: '#eef6ff',
            border: '1px solid #b6daff',
            color: '#0b5394',
            padding: '12px 16px',
            borderRadius: radius.md,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          Komisyon oranları <strong>tarihsel</strong> tutulur. Bir oran değişince
          (örn. Multinet %10→%5) <strong>"Yeni dönem oranı"</strong> ekleyin — eski tarihli
          satışlar eski oranla kalır, geçmiş raporlar <strong>bozulmaz</strong>. Satışı olan
          bir dönemin oranı geriye dönük değiştirilemez (Anayasa M.6).
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button
            onClick={openChannel}
            style={{
              padding: '10px 18px',
              background: '#c62828',
              color: 'white',
              border: 'none',
              borderRadius: radius.sm,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(198,40,40,0.25)',
            }}
          >
            + Yeni Kanal
          </button>
        </div>

        <div
          style={{
            background: colors.surface,
            borderRadius: radius.md,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          {kanallar.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: colors.textSecondary }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🏦</div>
              <div style={{ fontSize: 15, marginBottom: 4 }}>Henüz komisyon kanalı tanımlanmamış.</div>
              <div style={{ fontSize: 13 }}>
                <strong>+ Yeni Kanal</strong> ile başlayın.
              </div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 10, width: 32 }}></th>
                  <th style={{ padding: 10 }}>Ad</th>
                  <th style={{ padding: 10 }}>POS Tipi</th>
                  <th style={{ padding: 10 }}>Kategori</th>
                  <th style={{ padding: 10 }}>Güncel Oran</th>
                  <th style={{ padding: 10 }}>Geçerlilik</th>
                  <th style={{ padding: 10 }}>Dönem</th>
                  <th style={{ padding: 10 }}>Durum</th>
                  <th style={{ padding: 10, textAlign: 'right' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {kanallar.map((k) => {
                  const isAktif = k.aktif !== false;
                  const isOpen = !!expanded[k.pos_odeme_tipi];
                  const donemler = k.donemler || [];
                  return (
                    <React.Fragment key={`${k.pos_odeme_tipi}-${k.kategori}`}>
                      <tr
                        style={{
                          borderBottom: `1px solid ${colors.border}`,
                          opacity: isAktif ? 1 : 0.55,
                          background: isAktif ? 'white' : '#fafafa',
                        }}
                      >
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          <button
                            onClick={() => setExpanded({ ...expanded, [k.pos_odeme_tipi]: !isOpen })}
                            title={isOpen ? 'Dönem geçmişini gizle' : 'Dönem geçmişini göster'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}
                          >
                            {isOpen ? '▼' : '▶'}
                          </button>
                        </td>
                        <td style={{ padding: 10, fontWeight: 500 }}>{k.ad}</td>
                        <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 12 }}>{k.pos_odeme_tipi}</td>
                        <td style={{ padding: 10, color: '#555' }}>{kategoriLabel(k.kategori)}</td>
                        <td style={{ padding: 10, fontWeight: 600 }}>{fmtPct(k.guncel_oran)}</td>
                        <td style={{ padding: 10, color: '#666' }}>{k.guncel_gecerlilik}</td>
                        <td style={{ padding: 10, color: '#666' }}>{donemler.length}</td>
                        <td style={{ padding: 10 }}>
                          <span
                            style={{
                              background: isAktif ? '#d4edda' : '#f8d7da',
                              color: isAktif ? '#155724' : '#721c24',
                              padding: '2px 8px',
                              borderRadius: radius.lg,
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {isAktif ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => openPeriod(k)}
                            title="Yeni dönem oranı (geçmişi bozmaz)"
                            style={{
                              padding: '4px 10px',
                              background: '#0d6efd',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              marginRight: 4,
                            }}
                          >
                            ➕ Yeni Dönem
                          </button>
                          <button
                            onClick={() => openEdit(k)}
                            title="Ad / aktiflik düzenle (oran değişimi için Yeni Dönem)"
                            style={{
                              padding: '4px 10px',
                              background: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              marginRight: 4,
                            }}
                          >
                            ✏️ Düzenle
                          </button>
                          <button
                            onClick={() => togglePasif(k)}
                            style={{
                              padding: '4px 10px',
                              background: isAktif ? '#ffc107' : '#28a745',
                              color: isAktif ? '#333' : 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            {isAktif ? 'Pasifleştir' : 'Aktifleştir'}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0, background: '#fafbfc' }}>
                            <div style={{ padding: '8px 16px 12px 48px' }}>
                              <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
                                Oran geçmişi (tarihe göre uygulanır):
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ textAlign: 'left', color: '#888' }}>
                                    <th style={{ padding: 6 }}>Geçerlilik</th>
                                    <th style={{ padding: 6 }}>Oran</th>
                                    <th style={{ padding: 6 }}>Kod</th>
                                    <th style={{ padding: 6 }}>Satış</th>
                                    <th style={{ padding: 6 }}>Durum</th>
                                    <th style={{ padding: 6, textAlign: 'right' }}>İşlem</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {donemler
                                    .slice()
                                    .sort((a, b) => (a.gecerlilik_baslangic < b.gecerlilik_baslangic ? 1 : -1))
                                    .map((d) => (
                                      <tr key={d.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                                        <td style={{ padding: 6 }}>{d.gecerlilik_baslangic}</td>
                                        <td style={{ padding: 6, fontWeight: 600 }}>{fmtPct(d.komisyon_pct)}</td>
                                        <td style={{ padding: 6, fontFamily: 'monospace', color: '#888' }}>{d.kod}</td>
                                        <td style={{ padding: 6 }}>
                                          {(d.usage || 0) > 0 ? (
                                            <span style={{ background: '#fff3cd', color: '#856404', padding: '1px 6px', borderRadius: 8 }}>
                                              {d.usage}
                                            </span>
                                          ) : (
                                            <span style={{ color: '#aaa' }}>0</span>
                                          )}
                                        </td>
                                        <td style={{ padding: 6 }}>{d.aktif !== false ? 'Aktif' : 'Pasif'}</td>
                                        <td style={{ padding: 6, textAlign: 'right' }}>
                                          <button
                                            onClick={() => deletePeriod(d)}
                                            disabled={(d.usage || 0) > 0}
                                            title={(d.usage || 0) > 0 ? 'Satışı var, silinemez' : 'Dönemi sil'}
                                            style={{
                                              padding: '2px 8px',
                                              background: (d.usage || 0) > 0 ? '#ccc' : '#dc3545',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: 4,
                                              cursor: (d.usage || 0) > 0 ? 'not-allowed' : 'pointer',
                                              fontSize: 11,
                                            }}
                                          >
                                            🗑️ Sil
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ─── Yeni Kanal Modal ─────────────────────────────────────── */}
      {channelModal && (
        <ModalShell onClose={() => !saving && setChannelModal(false)} title="Yeni Komisyon Kanalı"
          hint="Kod sonradan değiştirilemez (referans bütünlüğü). POS Tipi, modpos raporundaki ödeme string'i ile BİREBİR eşleşmeli (örn. MULTİNET).">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Kod*</label>
              <input style={inputStyle} value={channelForm.kod} autoFocus maxLength={50}
                onChange={(e) => setChannelForm({ ...channelForm, kod: e.target.value.toUpperCase() })}
                placeholder="MULTINET" />
            </div>
            <div>
              <label style={labelStyle}>Ad*</label>
              <input style={inputStyle} value={channelForm.ad} maxLength={200}
                onChange={(e) => setChannelForm({ ...channelForm, ad: e.target.value })} placeholder="Multinet" />
            </div>
            <div>
              <label style={labelStyle}>POS Ödeme Tipi*</label>
              <input style={inputStyle} value={channelForm.pos_odeme_tipi} maxLength={100}
                onChange={(e) => setChannelForm({ ...channelForm, pos_odeme_tipi: e.target.value })}
                placeholder="MULTİNET (modpos string)" />
            </div>
            <div>
              <label style={labelStyle}>Kategori*</label>
              <select style={inputStyle} value={channelForm.kategori}
                onChange={(e) => setChannelForm({ ...channelForm, kategori: e.target.value })}>
                {KATEGORI_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>İlk Komisyon Oranı (%)*</label>
              <input style={inputStyle} type="text" inputMode="decimal" value={channelForm.komisyon_pct}
                onChange={(e) => setChannelForm({ ...channelForm, komisyon_pct: normalizeDecimalInput(e.target.value) })}
                placeholder="10" />
            </div>
            <div>
              <label style={labelStyle}>Geçerlilik Başlangıç*</label>
              <input style={inputStyle} type="date" value={channelForm.gecerlilik_baslangic}
                onChange={(e) => setChannelForm({ ...channelForm, gecerlilik_baslangic: e.target.value })} />
            </div>
          </div>
          <ModalError msg={channelError} />
          <ModalActions saving={saving} onCancel={() => setChannelModal(false)} onSave={submitChannel} />
        </ModalShell>
      )}

      {/* ─── Yeni Dönem Modal ─────────────────────────────────────── */}
      {periodModal && (
        <ModalShell onClose={() => !saving && setPeriodModal(null)} title={`Yeni Dönem Oranı — ${periodModal.kanal.ad}`}
          hint={`Bu oran seçtiğiniz tarihten itibaren geçerli olur. Öncesi mevcut ${fmtPct(periodModal.kanal.guncel_oran)} ile kalır — geçmiş raporlar bozulmaz.`}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Yeni Komisyon Oranı (%)*</label>
              <input style={inputStyle} type="text" inputMode="decimal" autoFocus value={periodForm.komisyon_pct}
                onChange={(e) => setPeriodForm({ ...periodForm, komisyon_pct: normalizeDecimalInput(e.target.value) })}
                placeholder="5" />
            </div>
            <div>
              <label style={labelStyle}>Geçerlilik Başlangıç*</label>
              <input style={inputStyle} type="date" value={periodForm.gecerlilik_baslangic}
                onChange={(e) => setPeriodForm({ ...periodForm, gecerlilik_baslangic: e.target.value })} />
            </div>
          </div>
          <ModalError msg={periodError} />
          <ModalActions saving={saving} onCancel={() => setPeriodModal(null)} onSave={submitPeriod} />
        </ModalShell>
      )}

      {/* ─── Düzenle Modal ────────────────────────────────────────── */}
      {editModal && (
        <ModalShell onClose={() => !saving && setEditModal(null)} title="Kanal Düzenle"
          hint="Yalnız ad ve aktiflik düzenlenir. Oran değişimi için 'Yeni Dönem Oranı' kullanın (geçmiş korunur).">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Kod (sabit)</label>
              <input style={{ ...inputStyle, background: '#f5f5f5', fontFamily: 'monospace', color: '#555' }}
                value={editModal.kod} disabled readOnly />
            </div>
            <div>
              <label style={labelStyle}>Ad*</label>
              <input style={inputStyle} value={editModal.ad} maxLength={200}
                onChange={(e) => setEditModal({ ...editModal, ad: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={editModal.aktif}
                  onChange={(e) => setEditModal({ ...editModal, aktif: e.target.checked })} />
                Aktif (güncel dönem)
              </label>
            </div>
          </div>
          <ModalError msg={editError} />
          <ModalActions saving={saving} onCancel={() => setEditModal(null)} onSave={submitEdit} />
        </ModalShell>
      )}
    </div>
  );
};

// ─── Ortak modal parçaları ───────────────────────────────────────
const ModalShell = ({ onClose, title, hint, children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ background: 'white', borderRadius: radius.md, padding: 24, minWidth: 480, maxWidth: 580, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 4 }}>{title}</h3>
      {hint && <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 16 }}>{hint}</p>}
      {children}
    </div>
  </div>
);

const ModalError = ({ msg }) =>
  msg ? (
    <div style={{ marginTop: 12, padding: '8px 12px', background: '#f8d7da', color: '#721c24', borderRadius: radius.sm, fontSize: 13 }}>
      {msg}
    </div>
  ) : null;

const ModalActions = ({ saving, onCancel, onSave }) => (
  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
    <button onClick={onCancel} disabled={saving}
      style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: radius.sm, cursor: saving ? 'not-allowed' : 'pointer' }}>
      Vazgeç
    </button>
    <button onClick={onSave} disabled={saving}
      style={{ padding: '8px 16px', background: saving ? '#999' : styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
      {saving ? 'Kaydediliyor...' : 'Kaydet'}
    </button>
  </div>
);

export default KomisyonOranlariPage;
